import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update, push } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, CalendarDays, CheckCircle, XCircle, Clock, Package, ChevronRight } from "lucide-react";

const STATUS_META = {
  pending:   { label: "Pending",   color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-400" },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-700",   dot: "bg-green-500" },
  rejected:  { label: "Rejected",  color: "bg-red-100 text-red-600",       dot: "bg-red-400" },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700",     dot: "bg-blue-400" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500",     dot: "bg-gray-400" },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function BookingCard({ booking, mode, onAccept, onReject, onCancel, onComplete, acting }) {
  const sm = STATUS_META[booking.status] || STATUS_META.pending;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
        <Link to={`/listing/${booking.listingId}`} className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 text-sm truncate">{booking.listingTitle || "Rental Item"}</p>
        </Link>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ml-2 ${sm.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
          {sm.label}
        </span>
      </div>

      <div className="px-4 py-3 space-y-2">
        {/* Dates */}
        <div className="flex items-center gap-2 text-sm">
          <CalendarDays size={14} className="text-teal-500 flex-shrink-0" />
          <span className="text-gray-700 font-medium">{fmtDate(booking.startDate)}</span>
          <span className="text-gray-400">→</span>
          <span className="text-gray-700 font-medium">{fmtDate(booking.endDate)}</span>
          <span className="text-xs text-gray-400 ml-1">({booking.days}d)</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Rental</span>
          <span className="font-bold text-teal-700">₹{booking.totalPrice}</span>
        </div>
        {booking.deposit > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Deposit (refundable)</span>
            <span className="font-medium text-gray-700">₹{booking.deposit}</span>
          </div>
        )}
        {booking.deliveryType && booking.deliveryType !== "pickup" && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Delivery</span>
            <span className="font-medium text-gray-700">{booking.deliveryCharge > 0 ? `₹${booking.deliveryCharge}` : "Free"}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm font-bold border-t border-gray-100 pt-2 mt-1">
          <span className="text-gray-700">Total to pay</span>
          <span className="text-teal-700 text-base">₹{booking.totalPrice + (booking.deposit || 0) + (booking.deliveryType !== "pickup" ? (booking.deliveryCharge || 0) : 0)}</span>
        </div>

        {/* Person info */}
        {mode === "seller" && (
          <div className="text-xs text-gray-500 pt-1">
            Renter: <span className="font-semibold text-gray-700">{booking.renterName}</span>
            {booking.renterPhone && <span> · 📱 {booking.renterPhone}</span>}
          </div>
        )}
        {mode === "renter" && (
          <div className="text-xs text-gray-500 pt-1">
            Booked on {fmtDate(booking.createdAt)}
          </div>
        )}

        {/* Action buttons */}
        {mode === "seller" && booking.status === "pending" && (
          <div className="flex gap-2 pt-2">
            <button onClick={() => onAccept(booking)} disabled={acting === booking.id}
              className="flex-1 bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:bg-green-700">
              {acting === booking.id ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle size={14} /> Accept</>}
            </button>
            <button onClick={() => onReject(booking)} disabled={acting === booking.id}
              className="flex-1 bg-red-50 text-red-600 border border-red-200 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:bg-red-100">
              <XCircle size={14} /> Reject
            </button>
          </div>
        )}
        {mode === "seller" && booking.status === "confirmed" && (
          <button onClick={() => onComplete(booking)} disabled={acting === booking.id}
            className="w-full bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-blue-700 disabled:opacity-50">
            <Package size={14} /> Mark as Completed
          </button>
        )}
        {mode === "renter" && booking.status === "pending" && (
          <button onClick={() => onCancel(booking)} disabled={acting === booking.id}
            className="w-full bg-gray-100 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-200 disabled:opacity-50">
            Cancel Request
          </button>
        )}
        {mode === "renter" && booking.status === "confirmed" && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 font-medium flex items-center gap-1.5">
            <CheckCircle size={13} /> Confirmed! Contact seller to arrange pickup/delivery.
          </div>
        )}
      </div>
    </div>
  );
}

export default function RentalBookings() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [tab, setTab] = useState("renter"); // "renter" | "seller"
  const [renterBookings, setRenterBookings] = useState([]);
  const [sellerBookings, setSellerBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // booking id being acted on

  const loadBookings = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const snap = await get(ref(db, "rentalBookings"));
      if (!snap.exists()) { setRenterBookings([]); setSellerBookings([]); setLoading(false); return; }

      const aRenter = [], aSeller = [];
      Object.entries(snap.val()).forEach(([listingId, bookings]) => {
        Object.entries(bookings).forEach(([bookingId, b]) => {
          const entry = { id: bookingId, listingId, ...b };
          if (b.renterId === currentUser.uid) aRenter.push(entry);
          if (b.sellerId === currentUser.uid) aSeller.push(entry);
        });
      });
      aRenter.sort((a, b) => b.createdAt - a.createdAt);
      aSeller.sort((a, b) => b.createdAt - a.createdAt);
      setRenterBookings(aRenter);
      setSellerBookings(aSeller);
    } catch (e) { }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  async function handleAccept(booking) {
    setActing(booking.id);
    try {
      await update(ref(db, `rentalBookings/${booking.listingId}/${booking.id}`), { status: "confirmed" });
      await push(ref(db, `notifications/${booking.renterId}`), {
        type: "rental_confirmed",
        message: `✅ Your rental booking for "${booking.listingTitle}" (${booking.startDate} → ${booking.endDate}) has been confirmed!`,
        listingId: booking.listingId, createdAt: Date.now(), read: false,
      });
      setSellerBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: "confirmed" } : b));
    } catch (e) { alert("Failed to accept booking. Try again."); }
    setActing(null);
  }

  async function handleReject(booking) {
    if (!window.confirm("Reject this booking request? The dates will be unblocked.")) return;
    setActing(booking.id);
    try {
      await update(ref(db, `rentalBookings/${booking.listingId}/${booking.id}`), { status: "rejected" });
      // Unblock dates
      const unblockedUpdates = {};
      const cur = new Date(booking.startDate), end = new Date(booking.endDate);
      while (cur < end) {
        unblockedUpdates[`listings/${booking.listingId}/rental/blockedDates/${cur.toISOString().split("T")[0]}`] = null;
        cur.setDate(cur.getDate() + 1);
      }
      await update(ref(db), unblockedUpdates);
      // Notify renter
      await push(ref(db, `notifications/${booking.renterId}`), {
        type: "rental_rejected",
        message: `❌ Your rental booking for "${booking.listingTitle}" was not accepted. Please choose different dates.`,
        listingId: booking.listingId, createdAt: Date.now(), read: false,
      });
      setSellerBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: "rejected" } : b));
    } catch (e) { alert("Failed to reject booking. Try again."); }
    setActing(null);
  }

  async function handleComplete(booking) {
    if (!window.confirm("Mark this rental as completed?")) return;
    setActing(booking.id);
    try {
      await update(ref(db, `rentalBookings/${booking.listingId}/${booking.id}`), { status: "completed" });
      await push(ref(db, `notifications/${booking.renterId}`), {
        type: "rental_completed",
        message: `📦 Your rental of "${booking.listingTitle}" has been marked as completed. Please return the item.`,
        listingId: booking.listingId, createdAt: Date.now(), read: false,
      });
      setSellerBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: "completed" } : b));
    } catch (e) { alert("Failed. Try again."); }
    setActing(null);
  }

  async function handleCancel(booking) {
    if (!window.confirm("Cancel this booking request?")) return;
    setActing(booking.id);
    try {
      await update(ref(db, `rentalBookings/${booking.listingId}/${booking.id}`), { status: "cancelled" });
      // Unblock dates
      const unblockedUpdates = {};
      const cur = new Date(booking.startDate), end = new Date(booking.endDate);
      while (cur < end) {
        unblockedUpdates[`listings/${booking.listingId}/rental/blockedDates/${cur.toISOString().split("T")[0]}`] = null;
        cur.setDate(cur.getDate() + 1);
      }
      await update(ref(db), unblockedUpdates);
      await push(ref(db, `notifications/${booking.sellerId}`), {
        type: "rental_cancelled",
        message: `🚫 Rental booking for "${booking.listingTitle}" was cancelled by the renter.`,
        listingId: booking.listingId, createdAt: Date.now(), read: false,
      });
      setRenterBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: "cancelled" } : b));
    } catch (e) { alert("Failed. Try again."); }
    setActing(null);
  }

  const activeList = tab === "renter" ? renterBookings : sellerBookings;
  const pendingSellerCount = sellerBookings.filter(b => b.status === "pending").length;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 sticky top-14 z-10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Rental Bookings</h2>
          <p className="text-xs text-gray-400">Manage your rental activity</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-100 px-4">
        {[
          { key: "renter", label: "My Rentals" },
          { key: "seller", label: "Requests Received", badge: pendingSellerCount },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-3 text-sm font-semibold border-b-2 transition flex items-center justify-center gap-1.5 ${tab === t.key ? "border-teal-500 text-teal-600" : "border-transparent text-gray-400"}`}>
            {t.label}
            {t.badge > 0 && (
              <span className="bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeList.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="font-semibold text-gray-500">
              {tab === "renter" ? "No rental bookings yet" : "No booking requests yet"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {tab === "renter" ? "Browse rental listings and book one!" : "When renters book your listings, they'll appear here."}
            </p>
            {tab === "renter" && (
              <Link to="/browse" className="inline-block mt-4 bg-teal-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold">
                Browse Rentals
              </Link>
            )}
          </div>
        ) : (
          activeList.map(booking => (
            <BookingCard
              key={booking.id}
              booking={booking}
              mode={tab}
              onAccept={handleAccept}
              onReject={handleReject}
              onComplete={handleComplete}
              onCancel={handleCancel}
              acting={acting}
            />
          ))
        )}
      </div>
    </div>
  );
}
